/* eslint-disable prettier/prettier */
import { useCallback, useMemo } from 'react';

import {
  CustomTableSortOrder,
  CustomTableSortState
} from '@components/custom-table/types';
import { STORAGE_KEYS } from '@constants/storage';
import { useAppDispatch, useAppSelector } from '@core/store';
import { DateRangeType } from '@features/dashboard/types';
import { RoleType } from '@features/team-management/constants';
import { IdLabelValue } from '@types';
import { addOneDayToDateString, convertDdMmYyyyToYyyyMmDd } from '@utils/date';
import { buildQueryString } from '@utils/queryStringBuilder';
import { getLocalStorageItem } from '@utils/storage';
import { StatusEntity } from 'types/lead-management';

import {
  UNASSIGNED_FILTER_ID,
  UNASSIGNED_FILTER_OPTION
} from '../constants/common';
import {
  useGetAgentsQuery,
  useGetStatusOptionsQuery,
  useGetTagsQuery
} from '../queries/LeadManagementQuery';
import {
  AdsSelectMode,
  setLeadAdsSelectMode,
  setLeadDateRange,
  setLeadDateRangeType,
  setLeadExcludedAdIds,
  setLeadListPage,
  setLeadSearch,
  setLeadSelectedAdIds,
  setLeadSelectedAssigneeIds,
  setLeadSelectedSources,
  setLeadSelectedStatusIds,
  setLeadSelectedTagIds,
  setLeadSort,
  setLeadStuckDaysFilter,
  StuckDaysFilterState
} from '../slices/leadSlice';
import {
  DaysStuckOp,
  isDaysStuckFilterComplete
} from '../utils/daysStuckFilter';
import useAdsFilter, { UseAdsFilterResult } from './useAdsFilter';
import useLeadSources from './useLeadSources';

export { DaysStuckOp } from '../utils/daysStuckFilter';

export type LeadListFilters = {
  search?: string;
  source?: string;
  status?: string;
  tags?: string;
  assignee?: string;
  unassigned?: boolean;
  ads_include?: string;
  ads_exclude?: string;
  start_date?: string;
  end_date?: string;
  days_stuck?: string;
  days_stuck_op?: DaysStuckOp;
  sort_by?: string;
  sort_order?: CustomTableSortOrder;
};

export type UseLeadListFiltersResult = {
  search: string;
  setSearch: (s: string) => void;
  selectedSources: string[];
  setSelectedSources: (s: string[]) => void;
  selectedStatusIds: string[];
  setSelectedStatusIds: (s: string[]) => void;
  selectedTagIds: string[];
  setSelectedTagIds: (t: string[]) => void;
  selectedDateRange: DateRangeType;
  sourceOptions: IdLabelValue[];
  statusOptions: IdLabelValue[];
  tagOptions: IdLabelValue[];
  selectedAdIds: string[];
  setSelectedAdIds: (s: string[]) => void;
  adsSelectMode: AdsSelectMode;
  setAdsSelectMode: (mode: AdsSelectMode) => void;
  excludedAdIds: string[];
  setExcludedAdIds: (ids: string[]) => void;
  toQueryString: (
    overrides?: Record<string, string | number | boolean>
  ) => string;
  toParams: () => LeadListFilters;
  hasActiveFilters: boolean;
  statusList: StatusEntity[];
  assigneeOptions: IdLabelValue[];
  selectedAssigneeIds: string[];
  setSelectedAssigneeIds: (s: string[]) => void;
  dateRangeType: string;
  setDateRangeType: (type: string) => void;
  setDateRange: (start?: string, end?: string) => void;
  stuckDaysFilter: StuckDaysFilterState;
  setStuckDaysFilter: (filter: StuckDaysFilterState) => void;
  sortState: CustomTableSortState | null;
  setSort: (sortKey: string, sortOrder: CustomTableSortOrder) => void;
} & UseAdsFilterResult;

const useLeadListFilters = (): UseLeadListFiltersResult => {
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const dispatch = useAppDispatch();

  const currentUser = useAppSelector((state) => state.user.currentUser) as {
    user_id?: string;
    name?: string;
  };
  const rolesAndPermissions = useAppSelector(
    (state) => state.user.rolesAndPermissions
  ) as { role?: string };
  const isSalesAgent = rolesAndPermissions?.role === RoleType.SALES_AGENT;

  const adsFilter = useAdsFilter();

  // Read filter state from Redux store
  const search = useAppSelector((state) => state.lead.search);
  const selectedSources = useAppSelector((state) => state.lead.selectedSources);
  const selectedStatusIds = useAppSelector(
    (state) => state.lead.selectedStatusIds
  );
  const selectedTagIds = useAppSelector((state) => state.lead.selectedTagIds);
  const selectedAssigneeIds = useAppSelector(
    (state) => state.lead.selectedAssigneeIds
  );
  const selectedAdIds = useAppSelector((state) => state.lead.selectedAdIds);
  const adsSelectMode = useAppSelector((state) => state.lead.adsSelectMode);
  const excludedAdIds = useAppSelector((state) => state.lead.excludedAdIds);
  const dateRangeType = useAppSelector((state) => state.lead.dateRangeType);
  const selectedDateRange = useAppSelector(
    (state) => state.lead.selectedDateRange
  );
  const stuckDaysFilter = useAppSelector((state) => state.lead.stuckDaysFilter);
  const sort = useAppSelector((state) => state.lead.sort);

  // Dispatch-based setters
  const setSearch = useCallback(
    (s: string) => dispatch(setLeadSearch(s)),
    [dispatch]
  );
  const setSelectedSources = useCallback(
    (s: string[]) => dispatch(setLeadSelectedSources(s)),
    [dispatch]
  );
  const setSelectedStatusIds = useCallback(
    (s: string[]) => dispatch(setLeadSelectedStatusIds(s)),
    [dispatch]
  );
  const setSelectedTagIds = useCallback(
    (t: string[]) => dispatch(setLeadSelectedTagIds(t)),
    [dispatch]
  );
  const setSelectedAssigneeIds = useCallback(
    (s: string[]) => dispatch(setLeadSelectedAssigneeIds(s)),
    [dispatch]
  );
  const setSelectedAdIds = useCallback(
    (s: string[]) => dispatch(setLeadSelectedAdIds(s)),
    [dispatch]
  );
  const setAdsSelectMode = useCallback(
    (mode: AdsSelectMode) => dispatch(setLeadAdsSelectMode(mode)),
    [dispatch]
  );
  const setExcludedAdIds = useCallback(
    (ids: string[]) => dispatch(setLeadExcludedAdIds(ids)),
    [dispatch]
  );
  const setDateRangeType = useCallback(
    (type: string) => dispatch(setLeadDateRangeType(type)),
    [dispatch]
  );
  const setStuckDaysFilter = useCallback(
    (filter: StuckDaysFilterState) => dispatch(setLeadStuckDaysFilter(filter)),
    [dispatch]
  );
  const setSort = useCallback(
    (sortKey: string, sortOrder: CustomTableSortOrder) => {
      dispatch(setLeadSort({ sortBy: sortKey, sortOrder }));
      dispatch(setLeadListPage(0));
    },
    [dispatch]
  );

  // Options
  const { sourceOptions, getSourceId } = useLeadSources();

  const { data: statusResp } = useGetStatusOptionsQuery({ organizationId });
  const statusOptions: IdLabelValue[] = useMemo(
    () =>
      (statusResp as any[])?.map((s) => ({
        id: String(s.id),
        label: String(s.value ?? s.display_name ?? s.name ?? ''),
        value: String(s.id)
      })) || [],
    [statusResp]
  );

  const { data: tagResp } = useGetTagsQuery({ organizationId });
  const tagOptions: IdLabelValue[] = useMemo(
    () =>
      (tagResp as any[])?.map((t) => ({
        id: String(t.id),
        label: String(t.name ?? ''),
        value: String(t.id)
      })) || [],
    [tagResp]
  );

  const { data: assigneeResp } = useGetAgentsQuery({ organizationId });
  const assigneeOptions: IdLabelValue[] = useMemo(() => {
    const allAssignees =
      (assigneeResp?.sales_agents as any[])?.map((a) => ({
        id: String(a.user_id),
        label: String(a.name ?? ''),
        value: String(a.user_id)
      })) || [];
    if (isSalesAgent) {
      const uid = String(currentUser.user_id);
      const self = allAssignees.find((o) => o.value === uid);
      return self ? [self] : allAssignees;
    }
    return [UNASSIGNED_FILTER_OPTION, ...allAssignees];
  }, [assigneeResp, isSalesAgent, currentUser]);

  const setDateRange = useCallback(
    (start?: string, end?: string) => {
      dispatch(
        setLeadDateRange({
          startDate: start || '',
          endDate: end || ''
        })
      );
    },
    [dispatch]
  );

  const toParams = (): LeadListFilters => {
    // Use convertDdMmYyyyToYyyyMmDd to correctly parse dd-mm-yyyy format
    const startDate = convertDdMmYyyyToYyyyMmDd(selectedDateRange.startDate);

    // Add +1 day to end_date for all date range types (thisMonth, lastMonth, custom)
    const endDate = selectedDateRange.endDate
      ? addOneDayToDateString(selectedDateRange.endDate)
      : '';

    const adsParams: Pick<LeadListFilters, 'ads_include' | 'ads_exclude'> = {};
    if (adsSelectMode === 'exclude') {
      adsParams.ads_exclude = excludedAdIds.join(',');
    } else if (selectedAdIds.length) {
      adsParams.ads_include = selectedAdIds.join(',');
    }

    const isUnassigned = selectedAssigneeIds.includes(UNASSIGNED_FILTER_ID);
    const agentIds = selectedAssigneeIds.filter(
      (id) => id !== UNASSIGNED_FILTER_ID
    );

    const daysStuckParams: Pick<
      LeadListFilters,
      'days_stuck' | 'days_stuck_op'
    > = {};
    if (isDaysStuckFilterComplete(stuckDaysFilter)) {
      daysStuckParams.days_stuck_op = stuckDaysFilter.op;
      daysStuckParams.days_stuck =
        stuckDaysFilter.op === DaysStuckOp.BETWEEN
          ? `${stuckDaysFilter.value},${stuckDaysFilter.valueTo}`
          : String(stuckDaysFilter.value);
    }

    const sortParams: Pick<LeadListFilters, 'sort_by' | 'sort_order'> = {};
    if (sort?.sortBy) {
      sortParams.sort_by = sort.sortBy;
      sortParams.sort_order = sort.sortOrder;
    }

    return {
      search: search || undefined,
      // Filter UI tracks sources by name (for the Facebook-Ads slug check), but
      // the API expects source ids.
      source: selectedSources.length
        ? selectedSources.map(getSourceId).filter(Boolean).join(',')
        : undefined,
      status: selectedStatusIds.length
        ? selectedStatusIds.join(',')
        : undefined,
      tags: selectedTagIds.length ? selectedTagIds.join(',') : undefined,
      assignee: agentIds.length ? agentIds.join(',') : undefined,
      unassigned: isUnassigned || undefined,
      ...adsParams,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      ...daysStuckParams,
      ...sortParams
    };
  };

  const toQueryString = (
    overrides?: Record<string, string | number | boolean>
  ) => buildQueryString({ ...toParams(), ...(overrides || {}) });

  const hasActiveFilters = Boolean(
    search ||
      selectedSources.length ||
      selectedStatusIds.length ||
      selectedTagIds.length ||
      selectedAssigneeIds.length ||
      selectedAdIds.length ||
      adsSelectMode !== 'none' ||
      selectedDateRange.startDate ||
      selectedDateRange.endDate ||
      isDaysStuckFilterComplete(stuckDaysFilter)
  );

  return {
    search,
    setSearch,
    selectedSources,
    setSelectedSources,
    selectedStatusIds,
    setSelectedStatusIds,
    selectedTagIds,
    setSelectedTagIds,
    selectedAssigneeIds,
    setSelectedAssigneeIds,
    selectedAdIds,
    setSelectedAdIds,
    adsSelectMode,
    setAdsSelectMode,
    excludedAdIds,
    setExcludedAdIds,
    selectedDateRange,
    setDateRange,
    sourceOptions,
    statusOptions,
    tagOptions,
    toQueryString,
    toParams,
    hasActiveFilters,
    statusList: statusResp,
    assigneeOptions,
    dateRangeType,
    setDateRangeType,
    stuckDaysFilter,
    setStuckDaysFilter,
    sortState: sort,
    setSort,
    ...adsFilter
  };
};

export default useLeadListFilters;
